
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UseBreederProfileProps {
  breederId: string;
}

// Helper function to validate UUID format
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const useBreederProfile = ({ breederId }: UseBreederProfileProps) => {
  return useQuery({
    queryKey: ['breeder-profile', breederId],
    queryFn: async () => {
      // Check if breederId is a valid UUID
      if (!breederId || !isValidUUID(breederId)) {
        console.log('Invalid breeder ID provided:', breederId);
        throw new Error('Invalid breeder ID');
      }

      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          business_name,
          first_name,
          last_name,
          location,
          profile_image_url,
          cover_image_url,
          description,
          location_coordinates,
          breeds
        `)
        .eq('id', breederId)
        .single();

      if (error) {
        console.error('Error fetching breeder profile:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Breeder not found');
      }

      // Safely handle the breeds field - ensure it's a string array
      const breedsArray = Array.isArray(data.breeds) && 
        data.breeds.every(breed => typeof breed === 'string') 
        ? data.breeds as string[] 
        : [];

      return {
        id: data.id,
        name: data.business_name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Unknown Breeder',
        profileImage: data.profile_image_url,
        coverImage: data.cover_image_url,
        location: data.location || 'Location not specified',
        description: data.description || 'No description available.',
        locationCoordinates: data.location_coordinates,
        breeds: breedsArray
      };
    },
  });
};
